import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronDownIcon, ChevronRightIcon, CheckCircleIcon, UsersIcon, InfoIcon } from 'lucide-react';
import { mockPRs } from '../data/mockData';
import { RFPPanel } from '../components/procurement/RFPPanel';
import { StatusBadge } from '../components/StatusBadge';
// Mock EOI data that would be linked to a PR
const mockEOIData = {
  default: {
    status: 'Approved',
    submissionDeadline: '2023-10-20',
    evaluationCriteria: 'Experience in similar projects, financial capacity, technical capability, compliance with specifications',
    remarks: 'EOI published on tender board. 5 suppliers responded, 2 shortlisted after evaluation.',
    shortlistedSuppliers: [{
      id: '1',
      name: 'TechCorp Solutions',
      submissionDate: '2023-10-15',
      status: 'Approved'
    }, {
      id: '2',
      name: 'Global Logistics Inc',
      submissionDate: '2023-10-16',
      status: 'Approved'
    }]
  }
};
// Mock: track which PRs have had EOI executed (for demo, first Open Tender PR has EOI)
const mockEOIExecutedPRs = new Set(['pr-3']); // Example: PR-2023-003 has EOI executed
export function RFPAction() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [eoiExpanded, setEoiExpanded] = useState(true);
  const pr = mockPRs.find(p => p.id === id);
  if (!pr) {
    return <div className="flex flex-col items-center justify-center h-full" data-id="element-2141">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-2142">PR not found</h2>
        <button onClick={() => navigate('/rfp')} className="mt-4 text-indigo-600 hover:text-indigo-800" data-id="element-2143">
          Back to RFP List
        </button>
      </div>;
  }
  const isOpenTender = pr.procurementMethod === 'Open Tender';
  const isDirectPurchase = pr.procurementMethod === 'Direct Purchase';
  const eoiExecuted = isOpenTender && mockEOIExecutedPRs.has(pr.id);
  const eoiData = mockEOIData.default;
  // Determine RFPPanel props based on method and EOI status
  const getRfpPanelProps = () => {
    if (isOpenTender && eoiExecuted) {
      // EOI was executed — use EOI shortlisted suppliers
      return {
        eoiSkipped: false as const,
        eoiSuppliers: eoiData.shortlistedSuppliers.map(s => ({
          id: s.id,
          supplierId: `SUP-${s.id.padStart(3, '0')}`,
          supplierName: s.name
        })),
        maxSuppliers: undefined
      };
    }
    if (isOpenTender && !eoiExecuted) {
      // Open Tender but EOI was skipped — use Supplier Management
      return {
        eoiSkipped: true as const,
        eoiSuppliers: undefined,
        maxSuppliers: undefined
      };
    }
    if (isDirectPurchase) {
      // Direct Purchase — Supplier Management, max 1 supplier
      return {
        eoiSkipped: undefined,
        eoiSuppliers: undefined,
        maxSuppliers: 1
      };
    }
    return {
      eoiSkipped: undefined,
      eoiSuppliers: undefined,
      maxSuppliers: undefined
    };
  };
  const rfpProps = getRfpPanelProps();
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-2144">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2145">
        <div className="flex items-center space-x-4" data-id="element-2146">
          <button onClick={() => navigate('/rfp')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2147">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2148" />
          </button>
          <div data-id="element-2149">
            <div className="flex items-center space-x-3" data-id="element-2150">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-2151">
                Process RFP
              </h1>
              <StatusBadge status="Draft" data-id="element-2152" />
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2153">
              Request for Proposal for {pr.prNo}
            </p>
          </div>
        </div>
      </div>

      {/* PR Context Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-wrap gap-6" data-id="element-2154">
        <div data-id="element-2155">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2156">
            PR Number
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2157">{pr.prNo}</p>
        </div>
        <div data-id="element-2158">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2159">
            Originator
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2160">
            {pr.originator}
          </p>
        </div>
        <div data-id="element-2161">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2162">
            Method
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2163">
            {pr.procurementMethod}
          </p>
        </div>
        <div data-id="element-2164">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2165">
            Total Amount
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2166">
            ${pr.total.toLocaleString()}
          </p>
        </div>
      </div>

      {/* EOI Summary — only for Open Tender when EOI was executed */}
      {eoiExecuted && <div className="bg-white rounded-xl border border-emerald-200 shadow-sm overflow-hidden" data-id="element-2167">
          <button onClick={() => setEoiExpanded(!eoiExpanded)} className="w-full flex items-center justify-between p-5 bg-emerald-50 hover:bg-emerald-100 transition-colors" data-id="element-2168">
            <div className="flex items-center space-x-3" data-id="element-2169">
              <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-2170">
                <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-2171" />
              </div>
              <div className="text-left" data-id="element-2172">
                <h3 className="text-sm font-semibold text-emerald-900" data-id="element-2173">
                  EOI Completed
                </h3>
                <p className="text-xs text-emerald-700 mt-0.5" data-id="element-2174">
                  Expression of Interest was executed and approved for this PR
                </p>
              </div>
              <StatusBadge status={eoiData.status} data-id="element-2175" />
            </div>
            {eoiExpanded ? <ChevronDownIcon className="w-5 h-5 text-emerald-600" data-id="element-2176" /> : <ChevronRightIcon className="w-5 h-5 text-emerald-600" data-id="element-2177" />}
          </button>
          {eoiExpanded && <div className="p-5 space-y-5 border-t border-emerald-100" data-id="element-2178">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2179">
                <div data-id="element-2180">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2181">
                    Submission Deadline
                  </p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2182">
                    {eoiData.submissionDeadline}
                  </p>
                </div>
                <div data-id="element-2183">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2184">
                    Status
                  </p>
                  <div className="mt-1" data-id="element-2185">
                    <StatusBadge status={eoiData.status} data-id="element-2186" />
                  </div>
                </div>
                <div className="md:col-span-2" data-id="element-2187">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2188">
                    Evaluation Criteria
                  </p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2189">
                    {eoiData.evaluationCriteria}
                  </p>
                </div>
                <div className="md:col-span-2" data-id="element-2190">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2191">
                    Remarks
                  </p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2192">
                    {eoiData.remarks}
                  </p>
                </div>
              </div>
              <div data-id="element-2193">
                <div className="flex items-center space-x-2 mb-3" data-id="element-2194">
                  <UsersIcon className="w-4 h-4 text-emerald-600" data-id="element-2195" />
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-2196">
                    Shortlisted Suppliers from EOI
                  </h4>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2197">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-2198">
                    <thead className="bg-gray-50" data-id="element-2199">
                      <tr data-id="element-2200">
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2201">
                          Supplier Name
                        </th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2202">
                          Submission Date
                        </th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2203">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-2204">
                      {eoiData.shortlistedSuppliers.map(supplier => <tr key={supplier.id} data-id="element-2205">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2206">
                            {supplier.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600" data-id="element-2207">
                            {supplier.submissionDate}
                          </td>
                          <td className="px-4 py-3" data-id="element-2208">
                            <StatusBadge status={supplier.status} data-id="element-2209" />
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
        </div>}

      {/* Info note when Open Tender but EOI was not executed */}
      {isOpenTender && !eoiExecuted && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start" data-id="element-2210">
          <InfoIcon className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-2211" />
          <div data-id="element-2212">
            <p className="text-sm font-medium text-amber-900" data-id="element-2213">
              EOI was not executed for this PR
            </p>
            <p className="text-sm text-amber-700 mt-0.5" data-id="element-2214">
              Suppliers will be selected from the Supplier Management master
              list instead of EOI shortlisted suppliers.
            </p>
          </div>
        </div>}

      {/* Embedded RFP Panel */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-2215">
        <RFPPanel eoiSkipped={rfpProps.eoiSkipped} eoiSuppliers={rfpProps.eoiSuppliers} maxSuppliers={rfpProps.maxSuppliers} data-id="element-2216" />
      </div>
    </div>;
}